/**
 * Shared types for all loop player components
 * (youtube-loop-player, video-loop-player, audio-loop-player)
 */

/**
 * Represents a single loop segment in the media
 */
export interface Loop {
    id: string;
    label: string;
    start: number;
    end: number;
    color: string;
}

/**
 * Which boundary is being edited
 */
export type EditingBoundary = 'start' | 'end' | null;

/**
 * Current editing state
 */
export interface EditState {
    loopId: string | null;
    boundary: EditingBoundary;
}

/**
 * Complete player state for persistence
 */
export interface PlayerState {
    loops: Loop[];
    activeLoopId: string | null;
    loopEnabled: boolean;
    expanded: boolean;
    playbackSpeed: number;
    pitch?: number; // Optional for backwards compatibility
    countdownEnabled: boolean;
    lastPosition: number;
}

/**
 * Timing constants for all loop players
 */
export const TIMING = {
    /** Interval for updating current time display */
    FRAME_INTERVAL_MS: 16,
    /** Debounce delay for saving state to localStorage */
    DEBOUNCE_SAVE_MS: 500,
    /** Seconds before end to preview when using "preview end" feature */
    PREVIEW_BEFORE_END_S: 3,
    /** Countdown duration before playing */
    COUNTDOWN_SECONDS: 3,
} as const;

/**
 * Adjustment values for seeking and loop editing
 */
export const ADJUSTMENTS = {
    /** Normal seek amount in seconds */
    SEEK_NORMAL_S: 5,
    /** Fine seek amount (with Shift) in seconds */
    SEEK_FINE_S: 0.1,
    /** Normal loop boundary adjustment in seconds */
    LOOP_ADJUST_NORMAL_S: 0.5,
    /** Fine loop boundary adjustment (with Shift) in seconds */
    LOOP_ADJUST_FINE_S: 0.1,
    /** Playback speed adjustment step */
    SPEED_STEP: 0.1,
    /** Minimum playback speed */
    SPEED_MIN: 0.1,
    /** Maximum playback speed */
    SPEED_MAX: 2.0,
} as const;

/**
 * Timeline visualization constants
 */
export const TIMELINE = {
    /** Padding on each side of loop when zoomed (30%) */
    ZOOM_PADDING: 0.3,
    /** Minimum visible duration in seconds when zoomed */
    MIN_VISIBLE_DURATION: 10,
    /** Minimum loop duration in seconds */
    MIN_LOOP_DURATION: 0.1,
} as const;
